import React, { useState, useEffect, Fragment, useMemo } from "react";
import { useRouteMatch, useLocation } from "react-router-dom";
import {
  Button,
  Input,
  Col,
  Row,
  Container,
  Label,
  FormGroup,
} from "reactstrap";
import Select, { ValueType } from "react-select";
import CreatableSelect from 'react-select/creatable';
import { useForm, SubmitHandler } from "react-hook-form";
import TextareaAutosize from "react-textarea-autosize";
import DataPipeline from 'model/data_pipeline';
import DataPipelineGraph from "model/data_pipeline_graph";
import DataPipelineRunLog from 'model/data_pipeline_run_log';
import DataPipelineGraphView from '../components/DataPipelineGraphView';
import "./index.css";
import ApiCaller from "api/apiCaller";
import api from "api";
import Loading from "components/Loading";
import { Popup } from "components/Popup";
import * as utils from "../../../resources/utils"

const ScheduleDocsLink = "http://docs.datadex.ficus.ai/docs/data-pipelines/setup-pipeline#general-information";

const Airflow_Schedule_Options = [
  { label: 'On Demand', value: 'on_demand'},
  { label: 'Hourly', value: '@hourly' },
  { label: 'Daily', value: '@daily' },
  { label: 'Weekly', value: '@weekly' },
  { label: 'Monthly', value: '@monthly' },
  { label: 'Yearly', value: '@yearly' },
  { label: 'Custom', value: 'custom'}
];

const Source_Load_Strategies = [
  { label: 'Merge', value: 'merge' },
  { label: 'Overwrite', value: 'overwrite' },
  { label: 'Append', value: 'append' },
];

const Source_Extract_Mode = [
  { label: 'Full', value: 'full' },
  { label: 'Incremental', value: 'incremental' },
];

const Source_Export_Format = [
  { label: 'JSON', value: 'json' },
];

const Destination_Table_Partition_Column_Type = [
  { label: 'Day', value: 'DAY'},
  { label: 'Hour', value: 'HOUR'},
  { label: 'Month', value: 'MONTH'},
  { label: 'Year', value: 'YEAR'},
];

const Unix_Timestamp_Type_Filter = [
  { label: 'Seconds', value: 'seconds' },
  { label: 'Millisecond', value: 'millis' },
  { label: 'Microsecond', value: 'micros' },
];

const Data_Life_Cycle_Time_Unit = [
  { label: 'Month', value: 'MONTH' },
  { label: 'Year', value: 'YEAR' },
]
// 1: RDBMS
// 3: MongoDB
// 5: Email
const Template_To_BigQuery = [1, 3, 5]

interface DataPipelineEditProps {

};

const defaultGCPConfig = {
  destination_table_require_partition_filter: false,
  auto_update_schema: true,
  custom_staging_partition: false,
  is_partitioned_table: false,
}

const defaultDatapipeline:DataPipeline = {
  table_name: "",
  key_fields: "",
  source_config: {
    flatten_data: false
  },
  gcp_config: defaultGCPConfig,
  extra_config: {
    create_destination_table: false,
    include_batch_id: false,
    destination_table_schema_strict: true,
    setup_data_life_cycle: false,
    need_backup_table: false
  }
};

const defaultDatapipelineGraph:DataPipelineGraph = {
  tasks: [],
};

const defaultDataPipelineRunLog: DataPipelineRunLog = {
  id: 0,
  graph: defaultDatapipelineGraph,
};

interface ModalData {
  isOpen: boolean;
  title: string;
  type: string;
  description: string;
  onPressSubmit: Function;
  onPressCancel: Function;
};

type OptionType = { value: string, label: string };

const DataPipelineEdit = ({}:DataPipelineEditProps) => {
  const companyId: any = api.getCurrentCompanyId();
  const match = useRouteMatch("/data_pipeline/:action/:data_pipeline_id");
  const params: any = match?.params;
  const dataPipelineId = params?.data_pipeline_id;
  const action = params?.action;
  const inputDataPipelineType = new URLSearchParams(useLocation().search).get('data_pipeline_template_id') || '';
  const inputDataPipelineRunLogId = new URLSearchParams(useLocation().search).get('data_pipeline_run_log_id') || '';
  const [dataPipeline, setDataPipeline] = useState<DataPipeline>({
    ...defaultDatapipeline,
    data_pipeline_template_id: parseInt(inputDataPipelineType, 10),
  });
  const [dataPipelineRunLog, setDataPipelineRunLog] = useState<DataPipelineRunLog>({
    ...defaultDataPipelineRunLog,
  });
  const [isCreate, setIsCreate] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [datasources, setDatasources] = useState([]);
  const [datasourceTables, setDatasourceTables] = useState([]);
  const [datasourceTableKeyFields, setDatasourceTableKeyFields] = useState([]);
  const [GCSBuckets, setGCSBuckets] = useState([]);
  const [BigQueryDatasets, setBigQueryDatasets] = useState([]);
  const [checkStagingTableMessage, setCheckStagingTableMessage] = useState(null);
  const [checkStagingTableMessageColor, setCheckStagingTableMessageColor] = useState(null);
  const [checkDestinationTableMessage, setCheckDestinationTableMessage] = useState(null);
  const [checkDestinationTableMessageColor, setCheckDestinationTableMessageColor] = useState(null);

  const [disableButtonRun, setDisableButtonRun] = useState(false);
  const [disableButtonReload, setDisableButtonReload] = useState(false);
  const [displayRetryButton, setDisplayRetryButton] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);

  const [isExpressionValid, setIsExpressionValid] = useState(false);
  const [isScheduleExpressionChecked, setIsScheduleExpressionChecked] = useState(false);
  const [nextSchedules, setNextSchedules] = useState([]);
  const [scheduleValidationError, setScheduleValidationError] = useState('');

  const [modalData, setModalData] = useState<ModalData>({
    isOpen: false,
    title: "",
    type: "confirm",
    description: "",
    onPressCancel: () => {},
    onPressSubmit: () => {},
  });

  const { register, setValue, handleSubmit, formState: { errors } } = useForm<DataPipeline>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: dataPipeline
  });

  const handleOnClickReload = ()  => {
    setModalData({
      isOpen: true,
      title: "Confirmation",
      type: "confirm",
      description: `Reload this pipeline ?`,
      onPressSubmit: () => {reloadDataPipeline()},
      onPressCancel: () => {
        setModalData({ ...modalData, ...{ isOpen: false } });
      },
    });
  };

  const checkScheduleExpression = async () => {
    if (dataPipeline.schedule_time === 'custom') {
      const res:any = await ApiCaller.post(
        `helpers/schedule/validate_expression?company_id=${companyId}`,
        {'schedule_expression': dataPipeline.extra_config?.custom_schedule}
      )
      if (res.status == 'ok') {
        if (res.data.is_valid) {
          setIsExpressionValid(true);
          setNextSchedules(res.data.next_runs);
          setScheduleValidationError('');
        } else {
          setIsExpressionValid(false);
          setScheduleValidationError(res.data.error_message)
          return false
        }
        setIsScheduleExpressionChecked(true);
        return true
      }
    } else {
      return true
    }
  }

  const onSubmit = handleSubmit(data => {
    if (dataPipeline.schedule_time === 'custom') {
      if (isExpressionValid) {
        saveDataPipeline();
      } else {
        alert('Invalid fields! Please check again!')
    }} else {
      saveDataPipeline();
    }
  });

  const getDatasources = async () => {
    const res:any = await ApiCaller.get(
      `datasource?company_id=${companyId}&is_active=1&limit=100&page=0&sort_type=desc`
    );
    if (res.data && res.data.datasources) {
      setDatasources(res.data.datasources);
    }
  };

  const getDatasourceTables = async () => {
    if (action === 'create' && dataPipeline.source_datasource_id) {
      const res:any = await ApiCaller.get(
        `datasource/${dataPipeline.source_datasource_id}/database/tables?return_as_filter=true`
      );
      if (res.data && res.data.tables) {
        setDatasourceTables(res.data.tables);
      } else {
        setDatasourceTables([]);
      }
    }
  };

  const getDatasourceTableKeyFields = async () => {
    if (action !== 'view' && dataPipeline.table_name && dataPipeline.load_strategy == 'merge' && dataPipeline.data_pipeline_template_name?.includes('rdbms_')) {
      const res:any = await ApiCaller.get(
        `datasource/${dataPipeline.source_datasource_id}/database/table/${dataPipeline.table_name}/primary_keys?return_as_filter=true`
      );
      if (res.data && res.data.primary_keys) {
        setDatasourceTableKeyFields(res.data.primary_keys);
        setDataPipeline({...dataPipeline, key_fields: res.data.primary_keys[0].value});
      } else {
        setDatasourceTableKeyFields([]);
      }
    }
  };

  const getGCSBuckets = async () => {
    if (dataPipeline.dwh_datasource_id) {
      const res:any = await ApiCaller.get(
        `datasource/${dataPipeline.dwh_datasource_id}/google/gcs/buckets?return_as_filter=true`
      );
      if (res.data && res.data.gcs_buckets) {
        setGCSBuckets(res.data.gcs_buckets);
      } else {
        setGCSBuckets([]);
      }
    }
  };

  const getBigQueryDatasets = async () => {
    if (dataPipeline.dwh_datasource_id) {
      const res:any = await ApiCaller.get(
        `datasource/${dataPipeline.dwh_datasource_id}/google/bigquery/datasets?return_as_filter=true`
      );
      if (res.data && res.data.datasets) {
        setBigQueryDatasets(res.data.datasets);
      } else {
        setBigQueryDatasets([]);
      }
    }
  };

  const checkBigQueryStagingTableExists = async () => {
    const res:any = await ApiCaller.post(
      `datasource/${dataPipeline.dwh_datasource_id}/google/bigquery/check_table_exists`,
      {
        "dataset": dataPipeline.gcp_config?.staging_dataset,
        "table": dataPipeline.gcp_config?.staging_table,
        "type": "staging"
      }
    );
    setCheckStagingTableMessage(res.data.message);
    setCheckStagingTableMessageColor(res.data.message_color);
  }

  const checkBigQueryDestinationTableExists = async () => {
    const res:any = await ApiCaller.post(
      `datasource/${dataPipeline.dwh_datasource_id}/google/bigquery/check_table_exists`,
      {
        "dataset": dataPipeline.gcp_config?.destination_dataset,
        "table": dataPipeline.gcp_config?.destination_table,
        "type": "destination"
      }
    );
    setCheckDestinationTableMessage(res.data.message);
    setCheckDestinationTableMessageColor(res.data.message_color);
  }

  const getDataPipeline = async () => {
    let uri = `pipeline/${dataPipelineId}?company_id=${companyId}`;
    if (inputDataPipelineRunLogId) {
      uri = `${uri}&data_pipeline_run_log_id=${inputDataPipelineRunLogId}`
    }
    const res:any = await ApiCaller.get(uri);
    if (res.data && res.data.pipeline) {
      setDataPipeline(res.data.pipeline);
      setIsExpressionValid(true);

      // react hook form does not recognize field name, set manually
      setValue("name", res.data.pipeline.name)

      if (res.data.pipeline.status !== 'running' && (res.data.pipeline.tasks !== null || res.data.pipeline_run_log.status !== 'not_started')) {
        setDisableButtonRun(false);
        setDisableButtonReload(false);
      } else {
        setDisableButtonRun(true);
        setDisableButtonReload(true);
      }
      setLoading(false);
    }
    if (res.data && res.data.pipeline_run_log) {
      setDataPipelineRunLog(res.data?.pipeline_run_log);
    }
  };
  const runDataPipeline = async () => {
    const res:any = await ApiCaller.post(
      `pipeline/${dataPipelineId}/execute?company_id=${companyId}`
    );
    if (res.status === 'ok') {
      alert('Run command has been issued. The pipeline will run any minute from now.');
      setDisableButtonRun(true);
    }
  };

  const reloadDataPipeline = async () => {
    setModalData({ ...modalData, isOpen: false });
    const res:any = await ApiCaller.post(
      `pipeline/${dataPipelineId}/reload?company_id=${companyId}`
    );
    if (res.status === 'ok') {
      alert('Reload command has been issued. The pipeline will run any minute from now.');
      setDisableButtonReload(true);
    }
  };

  const retryDataPipeline = async () => {
    const res:any = await ApiCaller.post(
      `pipeline/${dataPipelineId}/retry?company_id=${companyId}`,
      {
        'dag_id': dataPipeline.dag_id
      }
    );
    if (res.status === 'ok') {
      alert('Retry command has been run!');
      setDisplayRetryButton(false);
    } else {
      alert(res.message)
    }
  };

  const goToDataPipelineEdit = () => {
    window.location.href = `/data_pipeline/edit/${dataPipelineId}`;
  };
  const goToDataPipelineLog = () => {
    window.location.href = `/data_pipeline_log/${dataPipelineId}`;
  };
  const saveDataPipeline = async () => {
    setIsSaving(true);
    const method = dataPipeline.id ? 'put' : 'post';
    const uri = dataPipeline.id ? `pipeline/${dataPipeline.id}` : 'pipeline';
    const res:any = await ApiCaller[method](`${uri}?company_id=${companyId}`, dataPipeline);
    if (res.status === 'ok') {
      if (isCreate) {
        window.location.href = `/data_pipeline/view/${res.data_pipeline_id}`;
        alert(res.message);
      } else {
        window.location.href = `/data_pipeline/view/${dataPipelineId}`;
      }
    } else {
      alert(res.message);
    }
    setIsSaving(false);
  };
  const setDataPipelineAttr = (attr:any, value:any, customAttrKey:any) => {
    // if custom schedule is changed, we need to force user to revalidate.
    if (attr === 'custom_schedule') {
      setIsExpressionValid(false);
    };

    if (!customAttrKey) {
      setDataPipeline({
        ...dataPipeline,
        [attr]: value,
      });
    } else {
      setDataPipeline({
        ...dataPipeline,
        [customAttrKey]: {
          // @ts-expect-error
          ...dataPipeline[customAttrKey],
          [attr]: value,
        },
      });
    }
  };
  const renderLayoutItem = (layoutItem:any) => {
    const shouldDisableInput = action === 'view';
    let componentLabel = layoutItem.label || layoutItem.attribute;
    componentLabel = componentLabel.replaceAll('_', ' ');
    // @ts-expect-error
    let componentValue = dataPipeline[layoutItem.attribute];
    if (layoutItem.customAttrKey) {
      // @ts-expect-error
      componentValue = dataPipeline[layoutItem.customAttrKey][layoutItem.attribute];
    }
    if (layoutItem.inputType === 'textarea' && layoutItem.isShow) {
      return (
        <FormGroup className={layoutItem.className}>
          <Label className="data-pipeline-detail__layout-item__label">{componentLabel}</Label>
          {layoutItem.tooltip &&
            utils.default.renderTooltip(layoutItem.tooltip)
          }
          <TextareaAutosize
            {...register(
              layoutItem.customAttrKey ? `${layoutItem.customAttrKey}.${layoutItem.attribute}` : layoutItem.attribute,
              layoutItem.validation
            )}
            className="form-control"
            onChange={e => setDataPipelineAttr(layoutItem.attribute, e.target.value, layoutItem.customAttrKey)}
            value={componentValue || ""}
            minRows={3}
            disabled={shouldDisableInput || layoutItem.isDisabledAfterCreate}
          />
          {layoutItem.error}
        </FormGroup>
      )
    }
    if (layoutItem.inputType === 'select' && layoutItem.isShow) {
      return (
        <FormGroup className={layoutItem.className}>
          <Label className="data-pipeline-detail__layout-item__label">{componentLabel}</Label>
          {layoutItem.tooltip &&
            utils.default.renderTooltip(layoutItem.tooltip)
          }
          <select
            {...register(
              layoutItem.customAttrKey ? `${layoutItem.customAttrKey}.${layoutItem.attribute}` : layoutItem.attribute,
              componentValue ? '' : layoutItem.validation
            )}
            className="form-control"
            onChange={e => setDataPipelineAttr(layoutItem.attribute, e.target.value, layoutItem.customAttrKey)}
            value={componentValue || {}}
            disabled={shouldDisableInput || layoutItem.isDisabledAfterCreate}
          >
            <option value="" hidden>-- Select one --</option>
            {layoutItem.options.map((option:any) =>
              <option key={option.value || option.id} value={option.value || option.id}>
                {option.label || option.name}
              </option>
            )}
          </select>
          {layoutItem.error}
        </FormGroup>
      )
    }

    if (layoutItem.inputType === 'select_with_search' && layoutItem.isShow) {
      return (
        <FormGroup className={layoutItem.className}>
          <Label className="data-pipeline-detail__layout-item__label">{componentLabel}</Label>
          {layoutItem.tooltip &&
            utils.default.renderTooltip(layoutItem.tooltip)
          }
          <CreatableSelect
            onChange={(selectedOption: ValueType<OptionType>) => {
              if (layoutItem.allowMultiSelect) {
                const values = (selectedOption as OptionType[])?.map((item: OptionType) => item.value);
                if (values !== undefined) {
                  setDataPipelineAttr(layoutItem.attribute, values.join(","), layoutItem.customAttrKey)
                } else {
                  setDataPipelineAttr(layoutItem.attribute, "", layoutItem.customAttrKey)
                }
              } else {
                const value = (selectedOption as OptionType).value;
                setDataPipelineAttr(layoutItem.attribute, value, layoutItem.customAttrKey)}
              }
            }
            value={layoutItem.selectedValue}
            options={layoutItem.options}
            disabled={true}
            isDisabled={shouldDisableInput || layoutItem.isDisabledAfterCreate}
            isLoading={layoutItem.options ? false : true}
            isMulti={layoutItem.allowMultiSelect}
            closeMenuOnSelect={layoutItem.allowMultiSelect ? false : true}
            isSearchable={true}
          />
          {layoutItem.selectedValue && layoutItem.error}
        </FormGroup>
      )
    }
    if (layoutItem.inputType === "checkbox" && layoutItem.isShow) {
      return (
        <FormGroup className={layoutItem.className}>
          <Label className="data-pipeline-detail__layout-item__label">{componentLabel}</Label>{' '}
          {layoutItem.tooltip &&
            utils.default.renderTooltip(layoutItem.tooltip)
          }
          <input
            {...register(
              layoutItem.customAttrKey ? `${layoutItem.customAttrKey}.${layoutItem.attribute}` : layoutItem.attribute,
              layoutItem.validation
            )}
            type="checkbox"
            onChange={e => setDataPipelineAttr(layoutItem.attribute, e.target.checked, layoutItem.customAttrKey)}
            value={componentValue || false}
            checked={componentValue}
            disabled={shouldDisableInput || layoutItem.isDisabledAfterCreate}
          />
          {layoutItem.error}
        </FormGroup>
      )
    }
    if (layoutItem.isShow) {
      return (
        <FormGroup className={layoutItem.className}>
          <Label className="data-pipeline-detail__layout-item__label">{componentLabel}</Label>
          {layoutItem.tooltip &&
            utils.default.renderTooltip(layoutItem.tooltip)
          }
          <input
            {...register(
              layoutItem.customAttrKey ? `${layoutItem.customAttrKey}.${layoutItem.attribute}` : layoutItem.attribute,
              layoutItem.validation
            )}
            className="form-control"
            onChange={e => setDataPipelineAttr(layoutItem.attribute, e.target.value, layoutItem.customAttrKey)}
            value={componentValue || ""}
            disabled={shouldDisableInput || layoutItem.isDisabledAfterCreate}
          />
          {layoutItem.attribute === 'custom_schedule' && isScheduleExpressionChecked &&
          (isExpressionValid ?
            <span className="data-pipeline__validation_success">
              <i className="material-icons">check</i>
              Valid
              <br/>Next schedules:
              {nextSchedules.map(schedule => (
                <li key={schedule}>{schedule}</li>
              )
              )}
            </span>
            :
            <span className="data-pipeline__validation_error">
              {scheduleValidationError}
            </span>
          )}
          {(layoutItem.attribute === "staging_table") &&
            <span className={`data-pipeline__validation_${checkStagingTableMessageColor}`}>{checkStagingTableMessage}</span>
          }
          {(layoutItem.attribute === "destination_table") &&
            <span className={`data-pipeline__validation_${checkDestinationTableMessageColor}`}>{checkDestinationTableMessage}</span>
          }
          {layoutItem.error}
        </FormGroup>
      );
    }
  };

  useEffect(() => {
    if (dataPipelineId && !isCreate) {
      getDataPipeline();
    }
  }, [dataPipelineId, isCreate]);

  useEffect(() => {
    if (inputDataPipelineType) {
      setDataPipeline({
        ...dataPipeline,
        data_pipeline_template_id: parseInt(inputDataPipelineType, 10),
      });
    }
  }, [inputDataPipelineType]);

  useEffect(() => {
    if (action && action === 'create') {
      setIsCreate(true);
      setLoading(false);
    } else {
      setIsCreate(false);
    }
  }, [action]);

  useEffect(() => {
    if (isCreate) {
      setDataPipeline({
        ...dataPipeline,
        company_id: companyId,
      });
    }
  }, [isCreate]);

  useEffect(() => {
    getDatasources();
  }, []);

  useEffect(() => {
    setDatasourceTables([]);
    if (action === "create") {
      setDataPipeline({...dataPipeline, table_name: "", key_fields: ""});
    }
    getDatasourceTables();
  }, [dataPipeline.source_datasource_id]);

  useEffect(() => {
    getDatasourceTableKeyFields();
  }, [dataPipeline.table_name, dataPipeline.load_strategy]);

  useEffect(() => {
    if (dataPipeline.status === 'failed') {
      setDisplayRetryButton(true);
    };
  }, [dataPipeline.status, displayRetryButton]);

  useEffect(() => {
    getGCSBuckets();
    getBigQueryDatasets();
    if (action === "create") {
      setDataPipeline({...dataPipeline, gcp_config: defaultGCPConfig});
    };
  }, [dataPipeline.dwh_datasource_id]);

  const sourceConfigurationGeneral = [
    { attribute: 'extract_mode', inputType: 'select', options: Source_Extract_Mode, className: 'required-field', customAttrKey: 'source_config',
      isShow: dataPipeline.table_name ? true : false,
      tooltip: '<b>Full</b>: load full data from source table\
          <br/><b>Incremental</b>: only extract data which has value of max fields greater than previous value \
          <br/>If you choose <b>Incremental</b>, you have to provide <b>Key Fields</b>',
      validation: { required: true},
      error: errors?.source_config?.extract_mode && <p className="data-pipeline__validation_error">Extract mode required</p>
    },

    { attribute: 'max_fields', className: dataPipeline.source_config?.extract_mode === 'incremental' ? 'required-field': '',
      isShow: dataPipeline.source_config?.extract_mode === 'incremental' && dataPipeline.table_name ? true : false,
      tooltip: 'Use to extract data from source table incremental.\
          <br/>Support multiple fields as max fields (separate by comma ",")\
          <br/>Required when <b>Extract mode</b> is <b>Incremental</b>',
      validation: { required: dataPipeline.source_config?.extract_mode === 'incremental' ? true : false},
      error: errors.max_fields && dataPipeline.source_config?.extract_mode === 'incremental' &&
        <p className="data-pipeline__validation_error">Max fields required when using incremental extract mode</p>
    },

    { attribute: 'load_strategy', inputType: 'select', options: Source_Load_Strategies, className: 'required-field',
      isShow: dataPipeline.table_name && dataPipeline.source_config?.extract_mode ? true : false,
      tooltip: '<b>Merge</b>: insert or update data \
          <br/><b>Overwrite</b>: replace data\
          <br/><b>Append</b>: insert data only \
          <br/>If you choose <b>Merge</b>, you have to provide <b>Key Fields</b> \
          <br/><b>Note</b>: You cannot change this option once pipeline is created.',
      isDisabledAfterCreate: action === 'edit' ? true : false,
      validation: { required: action === 'edit' ? false : true},
      error: errors.load_strategy && <p className="data-pipeline__validation_error">Load strategy required</p>
    },

    { attribute: 'key_fields',
      className: dataPipeline.load_strategy === 'merge' ? 'required-field': '',
      inputType: datasourceTableKeyFields.length > 0 ? 'select_with_search' : '',
      allowMultiSelect: true,
      options: datasourceTableKeyFields,
      selectedValue: dataPipeline.key_fields ? dataPipeline.key_fields.split(",").map(function(field) {
        return {"label": field, "value": field}
      }) : null,
      isShow: dataPipeline.load_strategy === 'merge' && dataPipeline.table_name ? true : false,
      tooltip: 'Usually it is primary key of a table.\
          <br/>Support multiple fields as key (separate by comma ",")\
          <br/>Required when <b>Load strategy</b> is <b>Merge</b>',
      // validation: { required: dataPipeline.load_strategy === 'merge' ? true : false},
      error: errors.key_fields && !dataPipeline.key_fields && dataPipeline.load_strategy === 'merge' &&
        <p className="data-pipeline__validation_error">Key fields required when using merge strategy</p>
    },
  ]
  const renderLayout = [
    {
      groupId: 1,
      name: 'General Information',
      renderCondition: true,
      layoutItems: [
        { attribute: 'name', className: 'required-field',
          isShow: true,
          tooltip: "Name of pipeline, recommend pattern: Load data {table_name} from {database}",
          validation: { required: true},
          error: errors.name && <p className="data-pipeline__validation_error">Name required</p>
        },
        { attribute: 'description', inputType: 'textarea', isShow: true, },
        // { attribute: 'tags', },
        { attribute: 'schedule_time', inputType: 'select', isShow: true, tooltip: 'HCM Timezone(+7)',
          options: Airflow_Schedule_Options
        },
        { attribute: 'custom_schedule', customAttrKey: 'extra_config',
          tooltip: "<br/>If you are <b>Cron Guru</b>, feel free to use cron expression, otherwise you can check docs for available custom options.",
          isShow: dataPipeline.schedule_time === 'custom' ? true : false,
          validation: { required: true, validate: checkScheduleExpression },
          error: errors.extra_config?.custom_schedule && <p className="data-pipeline__validation_error">Schedule expression required</p>
        },
      ],
    },

    {
      groupId: 2,
      name: 'Source RDBMS Information',
      renderCondition: dataPipeline.data_pipeline_template_id === 1,
      layoutItems: [
        { attribute: 'source_datasource_id', inputType: 'select', className: 'required-field',
          isShow: true,
          tooltip: '<b>Note</b>: You cannot change this option once pipeline is created.',
          options: datasources.filter((datasource: any) => datasource.datasource_type_code_name.includes('rdbms_')),
          isDisabledAfterCreate: action === 'edit' ? true : false,
          validation: { required: action === 'edit' ? false : true},
          error: errors.source_datasource_id && <p className="data-pipeline__validation_error">Source data required</p>
        },

        { attribute: 'table_name', inputType: 'select_with_search', className: 'required-field',
          tooltip: '<b>Note</b>: You cannot change this option once pipeline is created.',
          isShow: dataPipeline.source_datasource_id ? true : false,
          options: datasourceTables,
          selectedValue: dataPipeline.table_name ? {"label": dataPipeline.table_name, "value": dataPipeline.table_name} : {},
          isDisabledAfterCreate: action === 'edit' ? true : false,
          validation: { required: action === 'edit' ? false : true},
          error: errors.table_name && <p className="data-pipeline__validation_error">Table name required</p>
        },
        ...sourceConfigurationGeneral,
      ],
    },
    {
      groupId: 3,
      name: 'Source MongoDB Information',
      renderCondition: dataPipeline.data_pipeline_template_id === 3,
      layoutItems: [
        { attribute: 'source_datasource_id', inputType: 'select', className: 'required-field',
          isShow: true,
          tooltip: '<b>Note</b>: You cannot change this option once pipeline is created.',
          options: datasources.filter((datasource: any) => datasource.datasource_type_code_name === 'mongo'),
          isDisabledAfterCreate: action === 'edit' ? true : false,
          validation: { required: action === 'edit' ? false : true},
          error: errors.source_datasource_id && <p className="data-pipeline__validation_error">Source data required</p>
        },

        { attribute: 'source_database_name', className: 'required-field', customAttrKey: 'source_config',
          tooltip: '<b>Note</b>: You cannot change this option once pipeline is created.',
          isShow: dataPipeline.source_datasource_id ? true : false,
          isDisabledAfterCreate: action === 'edit' ? true : false,
          validation: { required: action === 'edit' ? false : true},
          error: errors?.source_config?.source_database_name && <p className="data-pipeline__validation_error">Database name required</p>
        },

        { attribute: 'table_name', label: 'Collection name', className: 'required-field',
          isShow: dataPipeline.source_config?.source_database_name ? true : false,
          tooltip: '<b>Note</b>: You cannot change this option once pipeline is created.',
          isDisabledAfterCreate: action === 'edit' ? true : false,
          validation: { required: action === 'edit' ? false : true},
          error: errors.table_name && <p className="data-pipeline__validation_error">Collection name required</p>
        },
        ...sourceConfigurationGeneral,

        { attribute: 'ignore_fields', customAttrKey: 'source_config',
          isShow: dataPipeline.table_name ? true : false,
          tooltip: 'ignore fields from a collection (separate by comma ",")'
        },

        { attribute: 'custom_schema', customAttrKey: 'source_config',
          isShow: dataPipeline.table_name ? true : false,
          tooltip: 'GCS uri where schema is located, if this field is provided, it will use the defined schema in the file instead of generating schema. \
                    <br/>Please make sure you provided valid BigQuery schema.\
                    <br/>GCS uri example: <i>gs://bucket_name/folder_name/schema_file.json</i>'
        },
        { attribute: 'flatten_data', inputType: 'checkbox', customAttrKey: 'source_config',
          isShow: dataPipeline.table_name ? true : false,
          tooltip: 'Covert all nested fields to separate columns with format <i>{parent}_{child}</i>\
                  <br/>Example: <i>{"field1": {"field2": "value"}}</i> --> <i>{"field1_field2": "value"}</i>'
        },
      ]
    },

    {
      groupId: 4,
      name: 'Transfer Information',
      renderCondition: dataPipeline.data_pipeline_template_id === 4,
      layoutItems: [
        { attribute: 'source_datasource_id', label: 'Source datasource', inputType: 'select', className: 'required-field',
          isShow: true,
          options: datasources.filter((datasource: any) => datasource.datasource_type_code_name === 'google_bigquery'),
          validation: { required: true},
          error: errors?.source_datasource_id && <p className="data-pipeline__validation_error">Source datasource required</p>
        },
        { attribute: 'dwh_datasource_id', label: 'Destination datasource', inputType: 'select', className: 'required-field',
          isShow: true,
          tooltip: '<b>Note</b>: You cannot change this option once pipeline is created.',
          options: datasources.filter(
            (datasource: any) => datasource.datasource_type_code_name.includes('rdbms_')
          ),
          isDisabledAfterCreate: action === 'edit' ? true : false,
          validation: { required: action === 'edit' ? false : true},
          error: errors.dwh_datasource_id && <p className="data-pipeline__validation_error">Source data required</p>
        },
        { attribute: 'destination_database_name', label: 'Database name', className: 'required-field', customAttrKey: 'extra_config',
          tooltip: '<b>Note</b>: You cannot change this option once pipeline is created.',
          isShow: dataPipeline.source_datasource_id ? true : false,
          isDisabledAfterCreate: action === 'edit' ? true : false,
          validation: { required: action === 'edit' ? false : true},
          error: errors?.source_config?.source_database_name && <p className="data-pipeline__validation_error">Database name required</p>
        },
        { attribute: 'destination_database_schema_name', label: 'Database schema name', customAttrKey: 'extra_config',
          isShow: dataPipeline.source_datasource_id ? true : false
        },
        { attribute: 'bigquery_source_dataset', label: 'BigQuery dataset', className: 'required-field', customAttrKey: 'gcp_config',
          isShow: dataPipeline.dwh_datasource_id ? true : false,
          tooltip: 'Dataset where main table is located',
          validation: { required: true},
          error: errors?.gcp_config?.destination_dataset && <p className="data-pipeline__validation_error">Dataset required</p>
        },
        { attribute: 'bigquery_source_table', label: 'BigQuery table', customAttrKey: 'gcp_config',
          tooltip: '<b>Note</b>: If this field is empty, it will use destination table name to lookup in BigQuery project.',
          isShow: dataPipeline.source_datasource_id ? true : false,
        },
        { attribute: 'table_name', label: 'Destination table', className: 'required-field',
          tooltip: '<b>Note</b>: You cannot change this option once pipeline is created.',
          isShow: dataPipeline.source_datasource_id ? true : false,
          isDisabledAfterCreate: action === 'edit' ? true : false,
          validation: { required: action === 'edit' ? false : true},
          error: errors.table_name && <p className="data-pipeline__validation_error">Destination table name required</p>
        },
        { attribute: 'custom_sql', label: 'Custom sql', inputType: 'textarea', customAttrKey: 'gcp_config',
          tooltip: '<b>Note</b>: A valid BigQuery query format.\
              <br/>If custom query is provided, it will generate temporary table before syncing data.',
          isShow: dataPipeline.source_datasource_id ? true : false,
        },
        { attribute: 'load_strategy', inputType: 'select', options: Source_Load_Strategies, className: 'required-field',
          isShow: true,
          tooltip: '<b>Merge</b>: insert or update data \
              <br/><b>Overwrite</b>: replace data\
              <br/><b>Append</b>: insert data only \
              <br/>If you choose <b>Merge</b>, you have to provide <b>Key Fields</b> \
              <br/><b>Note</b>: You cannot change this option once pipeline is created.',
          isDisabledAfterCreate: action === 'edit' ? true : false,
          validation: { required: action === 'edit' ? false : true},
          error: errors.load_strategy && <p className="data-pipeline__validation_error">Load strategy required</p>
        },

        { attribute: 'key_fields', className: dataPipeline.load_strategy === 'merge' ? 'required-field': '',
          isShow: dataPipeline.load_strategy === 'merge',
          tooltip: 'Usually it is primary key of a table.\
              <br/>Support multiple fields as key (separate by comma ",")\
              <br/>Required when <b>Load strategy</b> is <b>Merge</b>',
          validation: { required: dataPipeline.load_strategy === 'merge' ? true : false},
          error: errors.key_fields && dataPipeline.load_strategy === 'merge' &&
            <p className="data-pipeline__validation_error">Key fields required when using merge strategy</p>
        },

        { attribute: 'create_destination_table', label: 'Auto create destination table', inputType: 'checkbox', customAttrKey: 'extra_config',
          tooltip: '<b>Note</b>: If ticked, it will try to create destination table base on table from BigQuery',
          isShow: dataPipeline.dwh_datasource_id ? true : false
        },
        { attribute: 'destination_table_schema_strict', label: 'Follow destination schema strictly', inputType: 'checkbox', customAttrKey: 'extra_config',
          tooltip: '<b>Note</b>: If ticked, it will only sync data that match destination table schema',
          isShow: dataPipeline.dwh_datasource_id ? true : false
        },
        { attribute: 'include_batch_id', label: 'Include batch id when syncing data', inputType: 'checkbox', customAttrKey: 'extra_config',
          tooltip: '<b>Note</b>: If ticked, when syncing data, it will auto generate an id to assign for each batch',
          isShow: dataPipeline.dwh_datasource_id ? true : false
        },
        // data life cycle
        { attribute: 'setup_data_life_cycle', customAttrKey: 'extra_config', inputType: 'checkbox', isShow: true},
      ]
    },

    {
      groupId: 200,
      name: 'Data Life Cycle Setup',
      renderCondition: dataPipeline.extra_config?.setup_data_life_cycle,
      layoutItems: [
        { attribute: 'date_column', label: 'Date column', customAttrKey: 'extra_config',
          isShow: true,
        },
        { attribute: 'data_life_cycle_interval', label: 'Cycle Interval', customAttrKey: 'extra_config',
          isShow: true,
        },
        { attribute: 'data_life_cycle_time_unit', label: 'Cycle Time Unit', customAttrKey: 'extra_config',
          inputType: 'select',
          options: Data_Life_Cycle_Time_Unit,
          isShow: true,
        },
        { attribute: 'need_backup_table', customAttrKey: 'extra_config', inputType: 'checkbox', isShow: true},
      ]
    },

    {
      groupId: 5,
      name: 'Source Email Information',
      renderCondition: dataPipeline.data_pipeline_template_id === 5,
      layoutItems: [
        { attribute: 'source_datasource_id', inputType: 'select', className: 'required-field',
          isShow: true,
          tooltip: '<b>Note</b>: You cannot change this option once pipeline is created.',
          options: datasources.filter((datasource: any) => datasource.datasource_type_code_name === 'alert_email'),
          isDisabledAfterCreate: action === 'edit' ? true : false,
          validation: { required: action === 'edit' ? false : true},
          error: errors.source_datasource_id && <p className="data-pipeline__validation_error">Source data required</p>
        },

        { attribute: 'email_label', label: 'Email label name', className: 'required-field', customAttrKey: 'source_config',
          isShow: dataPipeline.source_datasource_id ? true : false,
          validation: { required: true },
          error: errors?.source_config?.email_label && <p className="data-pipeline__validation_error">Collection name required</p>
        },

        { attribute: 'email_date_filter', label: 'Start from', customAttrKey: 'source_config',
          isShow: dataPipeline.source_datasource_id ? true : false,
        }
      ]
    },
    {
      groupId: 97,
      name: 'Destination datasource',
      renderCondition: Template_To_BigQuery.some(template_id => template_id === dataPipeline.data_pipeline_template_id),
      layoutItems: [
        { attribute: 'dwh_datasource_id', label: 'Project', inputType: 'select', className: 'required-field',
          isShow: true,
          options: datasources.filter((datasource: any) => datasource.datasource_type_code_name === 'google_bigquery'),
          validation: { required: true},
          error: errors?.dwh_datasource_id && <p className="data-pipeline__validation_error">DWH source required</p>
        }
      ]
    },
    {
      groupId: 98,
      name: 'Google Cloud Storage Information',
      renderCondition: Template_To_BigQuery.some(template_id => template_id === dataPipeline.data_pipeline_template_id),
      layoutItems: [
        { attribute: 'gcs_bucket', label: 'Staging GCS bucket', className: 'required-field', customAttrKey: 'gcp_config',
          inputType: 'select_with_search',
          isShow: dataPipeline.dwh_datasource_id ? true : false,
          options: GCSBuckets,
          selectedValue: dataPipeline.gcp_config?.gcs_bucket ? {"label": dataPipeline.gcp_config?.gcs_bucket, "value": dataPipeline.gcp_config?.gcs_bucket} : {},
          tooltip: 'GCS bucket name',
          validation: { required: true},
          error: errors?.gcp_config?.gcs_bucket &&
            <p className="data-pipeline__validation_error">GCS bucket required</p>
        },
        { attribute: 'gcs_prefix', label: 'Staging GCS Prefix', customAttrKey: 'gcp_config',
          isShow: dataPipeline.gcp_config?.gcs_bucket ? true : false,
          tooltip: 'Prefix folder inside bucket, by default when data is extracted it will be stored by table name and hive partition.'
        }
      ],
    },
    {
      groupId: 99,
      name: 'BigQuery Staging Information',
      renderCondition: Template_To_BigQuery.some(template_id => template_id === dataPipeline.data_pipeline_template_id),
      layoutItems: [
        { attribute: 'staging_dataset', className: 'required-field', customAttrKey: 'gcp_config',
          inputType: 'select_with_search',
          isShow: dataPipeline.dwh_datasource_id ? true : false,
          options: BigQueryDatasets,
          selectedValue: dataPipeline.gcp_config?.staging_dataset ? {"label": dataPipeline.gcp_config?.staging_dataset, "value": dataPipeline.gcp_config?.staging_dataset} : {},
          tooltip: 'Staging dataset where stanging table will be located.',
          validation: { required: true},
          error: errors?.gcp_config?.staging_dataset && <p className="data-pipeline__validation_error">Staging dataset required</p>
        },

        { attribute: 'staging_table', className: 'required-field', customAttrKey: 'gcp_config',
          isShow: dataPipeline.gcp_config?.staging_dataset ? true : false,
          tooltip: 'Staging table name (external table)',
          validation: { required: false, validate: checkBigQueryStagingTableExists },
          error: errors?.gcp_config?.staging_table && <p className="data-pipeline__validation_error">Staging table required</p>
        },

        {
          attribute: 'custom_staging_partition', customAttrKey: 'gcp_config', inputType: 'checkbox',
          isShow: dataPipeline.gcp_config?.staging_dataset && dataPipeline.gcp_config?.staging_table ? true : false,
          tooltip: 'Default columns: <i>dt,hour</i>\
                <br/>Default format: <i>date/hour</i>\
                <br/><b>Note</b>: If you want to change this after pipeline is created, you have to clear GCS folder.',
        },

        { attribute: 'staging_table_partition_columns', customAttrKey: 'gcp_config',
          isShow: dataPipeline.gcp_config?.staging_dataset && dataPipeline.gcp_config?.staging_table
                  && dataPipeline.gcp_config?.custom_staging_partition ? true : false,
          tooltip: 'Partition columns of staging table, this will be used to stored data in GCS.\
                  <br/>Separate by comma ","\
                  <br/>Example: year, month, day, hour'
        },

        { attribute: 'staging_table_partition_format', customAttrKey: 'gcp_config',
          isShow: dataPipeline.gcp_config?.staging_dataset && dataPipeline.gcp_config?.staging_table
                && dataPipeline.gcp_config?.custom_staging_partition
                && dataPipeline.gcp_config.staging_table_partition_columns ? true : false,
          tooltip: 'Data type for partition columns of staging table, this will be used to load data to BigQuery.\
                <br/>Please input follow same order as <b>Staging Table Partition Columns</b>\
                <br/>Separate by slash "/"\
                <br/>Example: year/month/day/hour \
                <br/>Supported types: <i>year, month, day, hour, date</i>',
          className: dataPipeline.gcp_config?.staging_table_partition_columns ? 'required-field' : '',
          validation: { required: dataPipeline.gcp_config?.staging_table_partition_columns ? true : false},
          error: errors?.gcp_config?.staging_table_partition_format &&
            <p className="data-pipeline__validation_error"> Format required when using custom partition for staging table</p>
        },
      ],
    },
    {
      groupId: 100,
      name: 'BigQuery Destination Information',
      renderCondition: Template_To_BigQuery.some(template_id => template_id === dataPipeline.data_pipeline_template_id),
      layoutItems: [
        { attribute: 'destination_dataset', className: 'required-field', customAttrKey: 'gcp_config',
          inputType: 'select_with_search',
          isShow: dataPipeline.dwh_datasource_id ? true : false,
          options: BigQueryDatasets,
          selectedValue: dataPipeline.gcp_config?.destination_dataset ? {"label": dataPipeline.gcp_config?.destination_dataset, "value": dataPipeline.gcp_config?.destination_dataset} : {},
          tooltip: 'Dataset where main table will be located',
          validation: { required: true},
          error: errors?.gcp_config?.destination_dataset && <p className="data-pipeline__validation_error">Destination dataset required</p>
        },
        { attribute: 'destination_table', className: 'required-field', customAttrKey: 'gcp_config',
          isShow: dataPipeline.gcp_config?.destination_dataset ? true : false,
          tooltip: 'Main table (native table)',
          validation: { required: false, validate: checkBigQueryDestinationTableExists },
          error: errors?.gcp_config?.destination_table && <p className="data-pipeline__validation_error">Destination table required</p>
        },
        { attribute: 'auto_update_schema', inputType: 'checkbox', customAttrKey: 'gcp_config',
          isShow: dataPipeline.gcp_config?.destination_table ? true : false,
          tooltip: 'When a source table has new fields, it will auto add new fields to destination table.\
                <br/><b>Note</b>: If alert channel is set, you will be notified when schema is changed.'
        },

        { attribute: 'is_partitioned_table', inputType: 'checkbox', customAttrKey: 'gcp_config',
          isShow: dataPipeline.gcp_config?.destination_table ? true : false,
          tooltip: 'If you want to change this option after pipeline is created, you have to do some extra steps.\
                  <br/>Currently, BigQuery does not support changing partition option of a table after it is created.\
                  You will have to change it manually in BigQuery!'
        },

        // { attribute: 'destination_table_friendly_name', label: 'Table friendly name', customAttrKey: 'gcp_config' },
        // { attribute: 'destination_table_description', label: 'Table description', customAttrKey: 'gcp_config' },

        { attribute: 'destination_table_partition_column_field', customAttrKey: 'gcp_config',
          isShow: dataPipeline.gcp_config?.destination_table && dataPipeline.gcp_config?.is_partitioned_table ? true : false,
          tooltip: 'Date or Timestamp column to use for partion destination table. See note at <b>Is Partition Table</b>',
          className: dataPipeline.gcp_config?.is_partitioned_table ? 'required-field' : '',
          validation: { required: dataPipeline.gcp_config?.is_partitioned_table ? (action === 'edit' ? false : true) : false},
          error: errors?.gcp_config?.destination_table_partition_column_field &&
            <p className="data-pipeline__validation_error"> Please input partition column</p>
        },

        { attribute: 'destination_table_partition_column_type', inputType: 'select', customAttrKey: 'gcp_config',
          tooltip: '<b>Note</b>: You cannot change this option once pipeline is created. See note at <b>Is Partition Table</b>',
          options: Destination_Table_Partition_Column_Type,
          isShow: dataPipeline.gcp_config?.destination_table && dataPipeline.gcp_config?.is_partitioned_table
              && dataPipeline.gcp_config?.destination_table_partition_column_field ? true : false,
          className: dataPipeline.gcp_config?.destination_table_partition_column_field ? 'required-field' : '',
          validation: { required: dataPipeline.gcp_config?.destination_table_partition_column_type ? true : false},
          error: errors?.gcp_config?.destination_table_partition_column_type &&
            <p className="data-pipeline__validation_error"> Please choose type for partition column</p>
        },

        { attribute: 'destination_table_require_partition_filter', inputType: 'checkbox', customAttrKey: 'gcp_config',
          isShow: dataPipeline.gcp_config?.destination_table && dataPipeline.gcp_config?.destination_table_partition_column_field ? true : false,
          tooltip: 'Force users to use partition column when they execute query.'
        },

        { attribute: 'unix_timestamp_columns', customAttrKey: 'gcp_config',
          isShow: dataPipeline.gcp_config?.destination_table ? true : false,
          tooltip: 'Convert columns that are stored as unix time (Integer) from source table to timestamp column\
                    <br/>Separate by comma ","'
        },
        { attribute: 'unix_timestamp_type', customAttrKey: 'gcp_config', inputType: 'select',
          options: Unix_Timestamp_Type_Filter,
          isShow: dataPipeline.gcp_config?.destination_table && dataPipeline.gcp_config?.unix_timestamp_columns ? true : false,
          tooltip: 'Type of unix timestamp columns',
          className: dataPipeline.gcp_config?.unix_timestamp_columns ? 'required-field' : '',
          validation: { required: dataPipeline.gcp_config?.unix_timestamp_columns ? true : false},
          error: errors?.gcp_config?.unix_timestamp_type &&
            <p className="data-pipeline__validation_error"> Please choose type for unix timestamp columns</p>
        },
        // { attribute: 'destination_table_labels', customAttrKey: 'gcp_config' },
      ],
    },
  ];

  // display loading
  if (loading) return <Loading />;

  return (
    <Container>
      <Popup
        type={modalData?.type}
        isOpen={modalData?.isOpen}
        title={modalData?.title}
        description={modalData?.description}
        onPressSubmit={modalData?.onPressSubmit}
        onPressCancel={modalData?.onPressCancel}
      />
      <Row>
        <Col xs={12}>
          <h1>{isCreate ? `Create new data pipeline` : `${dataPipeline.name}`}</h1>
        </Col>
        <Col xs={12}>
          <div className="data-pipeline-detail__control-buttons">
            {action === 'view' &&
              <Fragment>
                <Button color="primary" onClick={runDataPipeline} disabled={disableButtonRun}>
                  Run
                </Button>{' '}
                <Button color="primary" onClick={goToDataPipelineLog}>
                  View run log
                </Button>{' '}
                <Button color="primary" onClick={goToDataPipelineEdit}>
                  Edit pipeline
                </Button>{' '}
                <Button color="info" onClick={handleOnClickReload} disabled={disableButtonReload}>
                  Reload
                </Button>{' '}
                {/* {displayRetryButton &&
                  <Button color="warning" onClick={retryDataPipeline}>
                    Retry
                  </Button>
                } */}
              </Fragment>
            }
          </div>
        </Col>
        {action === 'view' &&
          <Col xs={12}>
            {dataPipeline.description &&
              <p><b>Description</b>: {dataPipeline.description}</p>
            }
            <ul>
              {/* <li><b>Run ID: #{dataPipelineRunLog.id}</b></li> */}
              <li><b>Status</b>: {utils.default.renderStatusLabel(dataPipelineRunLog.status)} {utils.default.renderStatusIcon(dataPipelineRunLog.status)} </li>
              {!inputDataPipelineRunLogId &&
                <li><b>Last run</b>: {dataPipeline.last_run}</li>
              }
              {!inputDataPipelineRunLogId &&
                <li><b>Next run</b>: {dataPipeline.next_run}</li>
              }
            </ul>
            {dataPipelineRunLog.status === 'not_started' && disableButtonRun && dataPipeline.data_pipeline_template_name !== 'generic_transformation' &&
              <div className="data-pipeline-detail__display_info">
                <div>Pipeline is being setup, please wait for a while (Usually it takes around 5 minutes).</div>
                <div>If schedule time is set, it will run automatically when it's ready, you will be notified in alert channel when it's done.</div>
              </div>
            }
            {(dataPipelineRunLog.graph || dataPipeline.data_pipeline_template_name === 'generic_transformation') &&
              <div className="data-pipeline-detail__graph-container">
                <DataPipelineGraphView
                  inputGraph={dataPipelineRunLog?.graph}
                  dataPipeline={dataPipeline}
                />
              </div>
            }
          </Col>
        }
        {!inputDataPipelineRunLogId &&
          <form className="data-pipeline__form" onSubmit={onSubmit}>
            {(action === 'create' || action === 'edit') &&
              <Button
                className="data-pipeline__submit_button"
                type="submit"
                value="submit"
                color={isSaving ? 'secondary' : 'success'}
                disabled={isSaving}
              >
                {isSaving ? 'Saving, please wait...' : 'Save pipeline'}
              </Button>
            }
            {renderLayout.map(layoutGroup => (
              <Fragment key={layoutGroup.groupId}>
                {layoutGroup.renderCondition &&
                  <div className="data-pipeline-detail__layout-group">
                    <div className="data-pipeline-detail__layout-group__name">{layoutGroup.name}</div>
                    <div className="data-pipeline-detail__layout-group__components">
                      {layoutGroup.layoutItems.map(layoutItem => (
                        <div key={layoutItem.attribute}>
                          {renderLayoutItem(layoutItem)}
                        </div>
                      ))}
                    </div>
                  </div>
                }
              </Fragment>
            ))}
          </form>
        }
      </Row>
    </Container>
  );
}

export default DataPipelineEdit;